import React, { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { alpha } from '@mui/material/styles';
import RenewalHelpCard from '../components/RenewalHelpCard';
import { useRenewalClick } from '../context/RenewalClickContext';
import { SCREENS, COVER_TYPES, RenewalJourneyEvent, RenewalJourneyLocation } from '../constants/renewalClick';
import {
  fireRenewalJourneyActivity,
} from '../api/renewalClickApi';
import VehicleSummaryCard from '../components/VehicleSummaryCard';
import RenewalOfferCard from '../components/RenewalOfferCard';
import PolicyDetailsAccordion from '../components/PolicyDetailsAccordion';
import {buildPolicyDetailRows} from '../mappers/renewalPolicyDetails.mapper';

const formatCurrency = (amount) => `₪${Number(amount).toLocaleString('he-IL')}`;

const styles = {
  greeting: (theme) => ({
    fontSize: theme.typography.pxToRem(26),
    fontWeight: 800,
    color: 'secondary.main',
    textAlign: 'left',
    mt: 2,
    mb: 0.5,
    '&::after': {
      content: '""',
      display: 'inline-block',
      width: '0.22em',
      height: '0.22em',
      borderRadius: '50%',
      backgroundColor: 'primary.main',
      marginInlineStart: '0.1em',
      verticalAlign: '0.1em',
    },
  }),
  subtitle: (theme) => ({
    fontSize: theme.typography.pxToRem(13),
    color: 'text.secondary',
    textAlign: 'left',
    mb: 2.5,
  }),
  disclaimer: {
    borderRadius: '12px',
    p: 1.75,
    mb: 2,
    display: 'flex',
    gap: 1,
    alignItems: 'flex-start',
    bgcolor: 'background.default',
  },
  disclaimerBold: { fontWeight: 'bold', color: 'secondary.main' },
  disclaimerText: (theme) => ({
    fontSize: theme.typography.pxToRem(13),
    color: 'text.secondary',
    lineHeight: 1.5,
  }),
  cta: (theme) => ({
    fontWeight: 'bold',
    fontSize: theme.typography.pxToRem(16),
    py: 1.5,
    borderRadius: '24px',
    width: '100%',
  }),
  securePayment: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.5,
    mt: 1.25,
    fontSize: theme.typography.pxToRem(12),
    color: 'text.secondary',
  }),
  confirmBox: (theme) => ({
    mt: 1.75,
    mb: 0.75,
    bgcolor: 'background.paper',
    border: 1,
    borderRadius: '16px',
    boxShadow: `0 6px 20px ${alpha(theme.palette.common.black, 0.05)}`,
    px: '18px',
    py: 2,
  }),
  confirmLabel: (theme) => ({
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 600,
    color: 'text.primary',
    lineHeight: 1.5,
  }),
  confirmError: (theme) => ({
    fontSize: theme.typography.pxToRem(12.5),
    fontWeight: 600,
    color: 'error.main',
    mb: 1,
    textAlign: 'left',
  }),
};

const OfferScreen = () => {
  const intl = useIntl();
  const m = (id, values) => intl.formatMessage({ id }, values);
  const {
    customer,
    vehicle,
    policy,
    pricing,
    meta,
    shortLinkKey,
    opportunityId,
    sameYearConfirmed,
    setSameYearConfirmed,
    setScreen,
  } = useRenewalClick();
  const sameYearFired = useRef(false);
  const [showConfirmError, setShowConfirmError] = useState(false);

  const handleConfirmChange = useCallback(
    (e) => {
      const checked = e.target.checked;
      setSameYearConfirmed(checked);
      if (checked) setShowConfirmError(false);
      if (checked && !sameYearFired.current && opportunityId) {
        sameYearFired.current = true;
        fireRenewalJourneyActivity(
          opportunityId,
          RenewalJourneyEvent.SAME_YEAR_CONFIRMED,
          { location: RenewalJourneyLocation.OFFER },
        );
      }
    },
    [setSameYearConfirmed, opportunityId],
  );

  const handleContinue = useCallback(async () => {
    if (!sameYearConfirmed) {
      setShowConfirmError(true);
      return;
    }
    if (opportunityId) {
      fireRenewalJourneyActivity(
        opportunityId,
        RenewalJourneyEvent.OFFER_CONFIRMED,
        { location: RenewalJourneyLocation.OFFER },
      );
    }
    setScreen(SCREENS.PAYMENT);
  }, [shortLinkKey, opportunityId, setScreen, sameYearConfirmed]);

  const handleDetailsExpand = useCallback(() => {
    if (opportunityId) {
      fireRenewalJourneyActivity(
        opportunityId,
        RenewalJourneyEvent.OFFER_DETAILS_OPENED,
        { location: RenewalJourneyLocation.OFFER },
      );
    }
  }, [opportunityId]);

  const offerLabels = {
    savingsLabel: m('renewalClick.offer.savingsLabel'),
    savingsCompare: m('renewalClick.offer.savingsCompare'),
    less: m('renewalClick.offer.less'),
    specialPrice: m('renewalClick.offer.specialPrice'),
    perYear: m('renewalClick.offer.perYear'),
    lastYearPrice: m('renewalClick.offer.lastYearPrice'),
    savingsBadge: m('renewalClick.offer.savingsBadge', {
      amount: formatCurrency(pricing.savings),
    }),
    breakdownMandatory: m('renewalClick.offer.breakdownMandatory', {
      amount: formatCurrency(pricing.mandatoryPremium),
    }),
    breakdownCasco: m(
      policy.coverType === COVER_TYPES.THIRD_PARTY
        ? 'renewalClick.offer.breakdownThirdParty'
        : 'renewalClick.offer.breakdownCasco',
      {amount: formatCurrency(pricing.cascoPremium)},
    ),
    breakdownServiceFee:
      pricing.serviceFee > 0
        ? m('renewalClick.offer.breakdownServiceFee', {
            amount: formatCurrency(pricing.serviceFee),
          })
        : undefined,
    breakdownOtherProducts: (pricing.otherProducts || []).map((p) =>
      m('renewalClick.offer.breakdownProduct', {name: p.name, amount: formatCurrency(p.amount)}),
    ),
    serviceFeeDisclaimer: m('renewalClick.offer.serviceFeeDisclaimer'),
    benefits: [
      m('renewalClick.offer.benefitSameDrivers'),
      m('renewalClick.offer.benefitNoQuestionnaire'),
      m('renewalClick.offer.benefitRenewalClick'),
    ],
  };

  const vehicleLabels = {
    vehicle: m('renewalClick.offer.vehicle'),
    policyEndDate: m('renewalClick.offer.policyEndDate'),
    daysRemaining: m('renewalClick.offer.daysRemaining', {
      days: meta.daysUntilExpiry,
    }),
  };

  const detailRows = buildPolicyDetailRows({customer, vehicle, policy, pricing}, m);

  return (
    <Box sx={{ px: 2, pb: 5, maxWidth: 560, mx: 'auto' }}>
      {/* Greeting */}
      <Typography sx={styles.greeting}>
        {m('renewalClick.offer.greeting', { name: customer.name })}
      </Typography>
      <Typography sx={styles.subtitle}>
        {m('renewalClick.offer.greetingSubtitle', {
          policyType: policy.type,
        })}
      </Typography>

      <VehicleSummaryCard
        vehicleInfo={vehicle}
        policyEndDate={policy.endDate}
        daysUntilExpiry={meta.daysUntilExpiry}
        labels={vehicleLabels}
      />

      <Box sx={{ mb: 2 }}>
        <RenewalOfferCard offerData={pricing} policy={policy} labels={offerLabels} />
      </Box>

      <Box sx={{ mb: 2 }}>
        <PolicyDetailsAccordion
          title={m('renewalClick.details.title')}
          rows={detailRows}
          onFirstExpand={handleDetailsExpand}
        />
      </Box>

      <Box
        sx={[
          styles.confirmBox,
          { borderColor: showConfirmError ? 'error.main' : 'divider' },
        ]}
      >
        <FormControlLabel
          sx={{ alignItems: 'center', m: 0 }}
          control={
            <Checkbox
              checked={sameYearConfirmed}
              onChange={handleConfirmChange}
              sx={{
                color: showConfirmError ? 'error.main' : 'divider',
                '&.Mui-checked': { color: 'secondary.main' },
              }}
            />
          }
          label={
            <Typography sx={styles.confirmLabel}>
              {m('renewalClick.offer.sameYearConfirm')}
            </Typography>
          }
        />
      </Box>
      {showConfirmError && (
        <Typography sx={styles.confirmError}>
          {m('renewalClick.offer.confirmError')}
        </Typography>
      )}

      <Box sx={{ mb: 2 }}>
        <RenewalHelpCard
          title={m('renewalClick.helpTitle')}
          description={m('renewalClick.helpDescription')}
        />
      </Box>

      {/* Disclaimer */}
      <Box sx={styles.disclaimer}>
        <InfoOutlinedIcon
          sx={{ color: 'primary.main', fontSize: 22, flexShrink: 0, mt: 0.25 }}
        />
        <Typography sx={styles.disclaimerText}>
          <Box component='span' sx={styles.disclaimerBold}>
            {m('renewalClick.offer.disclaimerTitle')}{' '}
          </Box>
          {m('renewalClick.offer.disclaimerText')}
        </Typography>
      </Box>

      {/* CTA */}
      <Button
        variant='contained'
        color='primary'
        disabled={false}
        onClick={handleContinue}
        sx={styles.cta}
      >
        {m('renewalClick.offer.cta')} ←
      </Button>
      <Typography sx={styles.securePayment}>
        <LockOutlinedIcon sx={{ fontSize: 14 }} />
        {m('renewalClick.securePayment')}
      </Typography>

      {process.env.REACT_APP_NODE_ENV === 'local' && (
        <button onClick={() => setScreen(SCREENS.SIGNING_PENDING)} style={{marginTop: 16}}>
          [DEV] Skip to signing
        </button>
      )}
    </Box>
  );
};

export default OfferScreen;
